import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subscription, forkJoin, tap } from 'rxjs';
import { AccountPlanDimensionsService } from '../../../accounting/service/data/account-plan-dimensions.service';
import { Features } from '../../../common/model/account-definition';
import { PostalAddress } from '../../../common/model/postal-address';
import { UserAccountService } from '../../../common/service/user-account.service';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { SelectionItem } from '../../../core/component/select/select.component';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../core/service/language.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Article } from '../../../rent/model/article';
import { Company } from '../../../rent/model/company';
import { CompanyBankAccount } from '../../../rent/model/company-bank-account';
import { CostBearerCompany } from '../../../rent/model/cost-bearer-company';
import { Creditor } from '../../../rent/model/creditor';
import { Tenant } from '../../../rent/model/tenant';
import { ArticleService } from '../../../rent/service/data/article.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { TenantService } from '../../../rent/service/data/tenant.service';
import { InvoiceDeliveryMethod, InvoiceRowType } from '../../model/const/enum';
import { CompanyBankAccountService } from '../../service/data/company-bank-account.service';
import { InvoiceService } from '../../service/data/invoice.service';
import { InvoiceRow } from '../../model/invoice-row';
import { InvoiceRowListContext } from '../invoice-row-list/invoice-row-list.component';
import { LeaseAgreementCategory, LeaseAgreementStatusToString, RentalObjectCategoryToString } from '../../../rent/model/const/enums';
import { InvoiceRecipientRecentLeaseAgreement } from '../../model/invoice-recipient-recent-lease-agreement';
import { FormControlEx } from '../../../core/component/forms/form-control';

@Component({
    selector: 'app-create-invoice-form',
    templateUrl: './create-invoice-form.component.html',
    styleUrls: ['./create-invoice-form.component.scss'],
    providers: [{ provide: AccountPlanDimensionsService }, CompanyBankAccountService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateInvoiceFormComponent implements OnInit, OnDestroy {
  private _articles: Article[];
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _notificationService: MessageService,
    public companyService: CompanyService,
    private _articleService: ArticleService,
    private _propertyService: PropertyService,
    private _accountPlanDimensionsService: AccountPlanDimensionsService,
    accountService: UserAccountService,
    filterService: TenantService,
    private _service: InvoiceService,
    private _navigationService: NavigationService,
    private _languageService: LanguageService,
    private _bankAccountService: CompanyBankAccountService
  ) {
    this._subscriptions.add(
      accountService.current.subscribe({
        next: account => (this.allowAttest = account.hasAccess(Features.AttestInvoices)),
      })
    );

    this.filterService = filterService;
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
    this._accountSub?.unsubscribe();
  }

  private _subscriptions = new Subscription();
  selectedCompany: Company;
  contacts: Creditor[] = [];
  articleSelection: SelectionItem[];
  favoriteArticles: Article[];
  articleCurrencyCode: string;
  addressModel = new PostalAddress();
  addressForm: FormGroup;
  debtorForm: FormGroup;
  form: FormGroup;
  appendixes: FormArray;
  filterService: IAutoCompleteService;
  rows: InvoiceRow[] = [];

  private _saving = false;

  get showFavorites(): boolean {
    return this.favoriteArticles?.length > 0;
  }

  onSubmit() {
    if (this._saving) return;

    // Address controls are added by the nested address component after form creation.
    // Mark the complete tree before rejecting the row requirement so their errors are visible too.
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.addressForm.markAllAsTouched();
    }

    if (this.rows.filter(ir => !ir.isInformation).length == 0) {
      this._notificationService.showError('INVOICE.ERROR_TEXT.NO_AMOUNT_ROW');
      return;
    }

    if (this.form.invalid) {
      return;
    } else if (this.form.valid) {
      this._saving = true;
      this._subscriptions.add(
        this._service
          .createGeneralInvoice(
            this.form.value,
            this.rows,
            this.appendixes.controls.map(f => f.value.data)
          )
          .subscribe(
            res => {
              this._saving = false;
              this._navigationService.navigateToList();
            },
            error => {
              this._saving = false;
              this._notificationService.showError(error);
            },
            () => {
              this._saving = false;
            }
          )
      );
    }
  }

  mimeTypes: string[] = DocumentTypeMimeTypes.PDF();

  cancel() {
    this._navigationService.navigateBack();
  }

  allowAttest: boolean;
  costBearerUsed: boolean;

  protected getFormGroup(): FormGroup {
    const form = this._fb.group({
      autoAttest: this._fc.create('INVOICE.CREATE.ATTEST_INSTANTLY', false),
      companyId: this._fc.createMandatory(
        'COMMON.COMPANY',
        this.selectedCompany?.id,
        'INVOICE.HELP_TEXT.GENERAL_INVOICE_SELECTION_LOCKED_TEXT'
      ),
      deliveryMethod: this._fc.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', InvoiceDeliveryMethod.Mail),
      labeling: this._fc.createMaxLength('LEASE_AGREEMENT.LABELING', null, 100),
      debtor: this.createDebtorForm(),
      dueDate: this._fc.createMandatory('INVOICE.DUE_DATE', LocalDate.fromDate(LocalDate.today().addDays(10))),
      accountingDate: this._fc.createMandatory('ACCOUNT.ACCOUNTING_DATE', LocalDate.today()),
      bankAccountId: this._fc.createMandatory('COMPANY.BANK_ACCOUNT.BANKGIRO_NUMBER', null),
      leaseAgreementId: this._fc.create(
        'COMMON.LEASE_AGREEMENT',
        null,
        null,
        null,
        'INVOICE.HELP_TEXT.GENERAL_INVOICE_LEASE_AGREEMENT_TEXT'
      ),
    });

    this._subscriptions.add(
      form.controls['deliveryMethod'].valueChanges.subscribe(val => {
        if (val === InvoiceDeliveryMethod.EmailWithPDF || val === InvoiceDeliveryMethod.EmailWithoutPDF) {
          this.debtorForm.controls['email'].setValidators([Validators.email, Validators.required]);
          this.debtorForm.controls['email'].updateValueAndValidity();
        } else {
          this.debtorForm.controls['email'].setValidators([Validators.email]);
          this.debtorForm.controls['email'].updateValueAndValidity();
        }
      })
    );

    this._subscriptions.add(
      form.controls['leaseAgreementId'].valueChanges.subscribe(val => {
        let leaseAgreement = this.matchingLeaseAgreements.find(l => l.id == val);
        if (val) {
          form.controls['deliveryMethod'].setValue(leaseAgreement.invoiceDeliveryMethod);
        }
        this.selectedLeaseAgreement = leaseAgreement;

        this.createRowsDataContext();
      })
    );

    return form;
  }

  private _accountSub: Subscription;
  onCompanySelected(item: AutoCompleteSelectionItem<Company>) {
    this.selectedCompany = item?.data;
    if (!this.selectedCompany) {
      return;
    }

    if (this.selectedCompany.currencyCode !== this.articleCurrencyCode) {
      this.updateArticleSelection();
      if (this.rows) {
        this.rows = [];
      }
    }

    this.updateContext();
    this._accountSub?.unsubscribe();
    this._accountSub = this._bankAccountService
      .getByCompany(this.selectedCompany.id)
      .subscribe(bankAccounts => this.setBankAccounts(bankAccounts));
  }

  private createDebtorForm(): FormGroup {
    this.debtorForm = this._fb.group({
      contactId: this._fc.createMandatory('INVOICE.RECIPIENT_NAME', null, 'INVOICE.HELP_TEXT.GENERAL_INVOICE_SELECTION_LOCKED_TEXT'),
      email: this._fc.createEmail('TENANT.EMAIL', null),
      counterpart: this._fc.createHidden(null),
      address: this.createAddressForm(),
    });
    return this.debtorForm;
  }

  onContactSelected(item: AutoCompleteSelectionItem) {
    const tenant = item?.data as Tenant;
    if (!tenant) {
      return;
    }
    this._subscriptions.add(
      this.filterService.getSelectionItem(tenant.id, { selectedItem: null, options: null }).subscribe(t => {
        this.addressForm.patchValue(t.data.invoiceAddress);
      })
    );
    this.debtorForm.controls['email'].setValue(tenant.emailInvoicing);
    this.debtorForm.controls['counterpart'].setValue(tenant.counterpart);
    this.addressForm.controls['name'].setValue(tenant.name);

    this.updateContext();
  }

  private createAddressForm(): FormGroup {
    this.addressForm = this._fb.group({});
    return this.addressForm;
  }

  accountSelection: SelectionItem[] = [];
  setBankAccounts(accounts: CompanyBankAccount[]) {
    this.accountSelection = accounts.filter(b => b.bankGiroNumber).map(b => new SelectionItem(b.bankGiroNumber, b.id));
    this.form?.controls['bankAccountId'].setValue(this.accountSelection.length ? this.accountSelection[0].value : null);
  }

  updateArticleSelection(): void {
    if (!this.selectedCompany || !this._articles) {
      return;
    }
    const currencyCode = this.selectedCompany.currencyCode;
    this.articleCurrencyCode = currencyCode;
    this.articleSelection = this._articles
      .filter(a => a.currencyCode === currencyCode)
      .map(a => new SelectionItem(a.code + ' ' + a.caption, a.id));
    this.favoriteArticles = this._articles.filter(a => a.currencyCode === currencyCode && a.favorite);
  }

  ngOnInit() {
    this.appendixes = this._fb.array([]);

    this._subscriptions.add(
      forkJoin([
        this._articleService.getList().pipe(
          tap(list => {
            this._articles = list;
          })
        ),
      ]).subscribe(() => {
        this.form ||= this.getFormGroup();
      })
    );
  }

  private matchingLeaseAgreements: InvoiceRecipientRecentLeaseAgreement[] = null;
  private selectedLeaseAgreement: InvoiceRecipientRecentLeaseAgreement = null;
  matchingLeaseAgreementsSelection: SelectionItem[] = null;
  private updateContext() {
    let companyId = this.selectedCompany?.id;
    let selectedRecipientId = this.debtorForm.controls['contactId'].value;

    this.rowsDataContext = null;
    if (!companyId || !selectedRecipientId) {
      return;
    }

    this._subscriptions.add(
      this._service.getMatchingLeaseAgreements(companyId, selectedRecipientId).subscribe(val => {
        this.matchingLeaseAgreements = val;
        this.matchingLeaseAgreementsSelection = null;
        if (val.length > 0) {
          // If there's only one lease agreement - it will be selected by default, and the selection itself is not shown.
          if (val.length > 1) {
            let selection = [];
            selection.push(new SelectionItem('-', null));
            this.matchingLeaseAgreements.map(a => selection.push(new SelectionItem(this.getMatchedLeaseAgreementText(a), a.id)));
            this.matchingLeaseAgreementsSelection = selection;
          }

          this.form.controls['leaseAgreementId'].setValue(val[0].id);
        } else {
          this.form.controls['leaseAgreementId'].setValue(null);
        }

        this.createRowsDataContext();
      })
    );
  }

  private getMatchedLeaseAgreementText(item: InvoiceRecipientRecentLeaseAgreement) {
    let str = item.leaseAgreementNumber;
    str += ` (${item.fromDate.toString()} -`;
    if (item.lastBillingDate) {
      str += ` ${item.lastBillingDate.toString()}`;
    }
    str += ` (${this._languageService.getText(LeaseAgreementStatusToString(item.status))})`;
    str += ') ';
    str += this._languageService.getText(RentalObjectCategoryToString(item.rentalObjectOneCategory));
    if (item.rentalObjectOneAddress?.street) {
      str += `: ${item.rentalObjectOneAddress.street}`;
    }

    return str;
  }

  rowsDataContext: InvoiceRowListContext = null;
  private createRowsDataContext() {
    this.rowsDataContext = {
      id: null,
      companyId: this.selectedCompany?.id,
      allowEditInvoiceRows: true,
      amountAndTextRows: this.rows,
      currencyCode: this.articleCurrencyCode,
      codingDefaultValues: {
        costBearer: this.selectedLeaseAgreement?.costBearer,
        costPlace: this.selectedLeaseAgreement?.costPlace,
        counterpart: this.debtorForm.controls['counterpart'].value,
        function: null,
        basedOnLeaseAgreement: this.selectedLeaseAgreement,
      },
    };
  }

  invoiceRowCreated(row: InvoiceRow) {
    this.rowsUpdated();
  }

  invoiceRowChanged(row: InvoiceRow) {
    this.rowsUpdated();
  }

  invoiceRowDeleted(id: string) {
    this.rowsUpdated();
  }

  invoiceRowReordered() {
    this.rowsUpdated();
  }

  rowsUpdated() {
    this.rows = [...this.rowsDataContext.amountAndTextRows];

    let isReadonly = this.rows.length > 0;
    (this.form.controls['leaseAgreementId'] as FormControlEx).isReadonly = isReadonly;
    (this.form.controls['companyId'] as FormControlEx).isReadonly = isReadonly;
    (this.debtorForm.controls['contactId'] as FormControlEx).isReadonly = isReadonly;
  }
}
